<template>
  <div class="file-uploader">
    <el-upload
      ref="uploadRef"
      :action="uploadUrl"
      :headers="uploadHeaders"
      :data="uploadData"
      :accept="acceptTypes"
      :multiple="multiple"
      :limit="limit"
      :file-list="fileList"
      :before-upload="beforeUpload"
      :on-success="handleSuccess"
      :on-error="handleError"
      :on-exceed="handleExceed"
      :on-remove="handleRemove"
      :list-type="listType"
      :disabled="disabled"
    >
      <template v-if="listType === 'picture-card'">
        <el-icon><Plus /></el-icon>
      </template>
      <template v-else>
        <el-button :disabled="disabled" type="primary">
          <el-icon><Upload /></el-icon>
          {{ buttonText }}
        </el-button>
      </template>
      
      <template #tip v-if="showTip">
        <div class="el-upload__tip">
          {{ tipText }}
        </div>
      </template>
    </el-upload>
  </div>
</template>

<script>
import { Plus, Upload } from '@element-plus/icons-vue'
import { mapGetters } from 'vuex'
import { FILE_UPLOAD_LIMITS } from '@/utils/constants'
import { validateFileSize, validateFileType } from '@/utils/validate'

export default {
  name: 'FileUploader',
  
  components: {
    Plus,
    Upload
  },
  
  props: {
    // 上传类型：image/file/video/audio
    uploadType: {
      type: String,
      default: 'image',
      validator: (value) => ['image', 'file', 'video', 'audio'].includes(value)
    },
    // 上传地址
    action: {
      type: String,
      default: ''
    },
    // 是否多选
    multiple: {
      type: Boolean,
      default: false
    },
    // 最大上传数量
    limit: {
      type: Number,
      default: 1
    },
    // 已上传文件列表
    modelValue: {
      type: Array,
      default: () => []
    },
    // 列表类型
    listType: {
      type: String,
      default: 'text',
      validator: (value) => ['text', 'picture', 'picture-card'].includes(value)
    },
    // 按钮文字
    buttonText: {
      type: String,
      default: '选择文件'
    },
    // 是否显示提示
    showTip: {
      type: Boolean,
      default: true
    },
    // 自定义提示文字
    customTip: {
      type: String,
      default: ''
    },
    // 是否禁用
    disabled: {
      type: Boolean,
      default: false
    },
    // 额外的上传参数
    data: {
      type: Object,
      default: () => ({})
    }
  },
  
  emits: ['update:modelValue', 'success', 'error', 'remove'],
  
  data() {
    return {
      fileList: []
    }
  },
  
  computed: {
    ...mapGetters('auth', ['token']),
    
    uploadUrl() {
      if (this.action) {
        return this.action
      }
      // 根据上传类型返回默认地址
      const baseUrl = process.env.VUE_APP_API_BASE_URL || ''
      return `${baseUrl}/api/upload/${this.uploadType}`
    },
    
    uploadHeaders() {
      return {
        'Authorization': `Bearer ${this.token}`
      }
    },
    
    uploadData() {
      return {
        type: this.uploadType,
        ...this.data
      }
    },
    
    acceptTypes() {
      const types = {
        image: FILE_UPLOAD_LIMITS.IMAGE.ACCEPT,
        file: FILE_UPLOAD_LIMITS.FILE.ACCEPT,
        video: FILE_UPLOAD_LIMITS.VIDEO.ACCEPT,
        audio: FILE_UPLOAD_LIMITS.AUDIO.ACCEPT
      }
      return types[this.uploadType] || '*'
    },
    
    maxSize() {
      const sizes = {
        image: FILE_UPLOAD_LIMITS.IMAGE.MAX_SIZE,
        file: FILE_UPLOAD_LIMITS.FILE.MAX_SIZE,
        video: FILE_UPLOAD_LIMITS.VIDEO.MAX_SIZE,
        audio: FILE_UPLOAD_LIMITS.AUDIO.MAX_SIZE
      }
      return sizes[this.uploadType] || 10 * 1024 * 1024
    },
    
    tipText() {
      if (this.customTip) {
        return this.customTip
      }
      
      const sizeText = this.formatSize(this.maxSize)
      const typeTexts = {
        image: `支持 JPG、PNG、GIF 格式，单个文件不超过 ${sizeText}`,
        file: `支持 PDF、DOC、DOCX、XLS、XLSX 格式，单个文件不超过 ${sizeText}`,
        video: `支持 MP4、AVI、MOV 格式，单个文件不超过 ${sizeText}`,
        audio: `支持 MP3、WAV、AAC 格式，单个文件不超过 ${sizeText}`
      }
      return typeTexts[this.uploadType] || `单个文件不超过 ${sizeText}`
    }
  },
  
  watch: {
    modelValue: {
      handler(newVal) {
        this.fileList = newVal || []
      },
      immediate: true,
      deep: true
    }
  },
  
  methods: {
    /**
     * 上传前校验
     */
    beforeUpload(file) {
      // 验证文件类型
      const typeValid = validateFileType(file, this.uploadType)
      if (!typeValid) {
        this.$message.error(`不支持的文件类型：${file.type}`)
        return false
      }
      
      // 验证文件大小
      const sizeValid = validateFileSize(file, this.maxSize)
      if (!sizeValid) {
        this.$message.error(`文件大小不能超过 ${this.formatSize(this.maxSize)}`)
        return false
      }
      
      return true
    },
    
    /**
     * 上传成功
     */
    handleSuccess(response, file, fileList) {
      if (response && response.code === 0) {
        this.$message.success('上传成功')
        
        // 更新文件列表
        const newFileList = fileList.map(item => {
          if (item.uid === file.uid && response.data) {
            return {
              ...item,
              url: response.data.url,
              path: response.data.path
            }
          }
          return item
        })
        
        this.fileList = newFileList
        this.$emit('update:modelValue', newFileList)
        this.$emit('success', response.data, file)
      } else {
        this.$message.error(response?.msg || '上传失败')
        this.handleError(new Error(response?.msg || '上传失败'), file, fileList)
      }
    },
    
    /**
     * 上传失败
     */
    handleError(error, file, fileList) {
      this.$message.error('上传失败，请重试')
      this.$emit('error', error, file)
      
      // 移除失败的文件
      const index = fileList.findIndex(item => item.uid === file.uid)
      if (index > -1) {
        fileList.splice(index, 1)
      }
    },
    
    /**
     * 超出限制
     */
    // eslint-disable-next-line no-unused-vars
    handleExceed(_files, _fileList) {
      this.$message.warning(`最多只能上传 ${this.limit} 个文件`)
    },
    
    /**
     * 移除文件
     */
    handleRemove(file, fileList) {
      this.fileList = fileList
      this.$emit('update:modelValue', fileList)
      this.$emit('remove', file)
    },
    
    /**
     * 格式化文件大小
     */
    formatSize(bytes) {
      if (bytes === 0) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },
    
    /**
     * 清空文件列表
     */
    clearFiles() {
      this.$refs.uploadRef?.clearFiles()
      this.fileList = []
      this.$emit('update:modelValue', [])
    }
  }
}
</script>

<style lang="scss" scoped>
.file-uploader {
  :deep(.el-upload) {
    width: 100%;
  }

  :deep(.el-upload--picture-card) {
    width: 178px;
    height: 178px;
    line-height: 178px;
    border-radius: 6px;
  }

  :deep(.el-upload-list--picture-card .el-upload-list__item) {
    width: 178px;
    height: 178px;
    border-radius: 6px;
  }
  
  :deep(.el-upload-list) {
    margin-top: 10px;
  }
  
  :deep(.el-upload__tip) {
    margin-top: 7px;
    color: #909399;
    font-size: 12px;
    line-height: 1.5;
  }
}
</style>
